import { useEffect, useState } from 'react'
import { Button, Input, Modal, Select, Textarea, useToast } from '@/design/components'
import { ApiError } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { useCompleteDeadline, useLawyers, useUpdateDeadline } from '../hooks'
import { STATUS_OPTIONS } from '../status'
import type { Deadline, DeadlineStatus } from '../types'

export interface EditDeadlineModalProps {
  deadline: Deadline | null
  onClose: () => void
  /** `false` → formularz tylko do odczytu (brak `deadlines.manage`). */
  canManage: boolean
}

/** Modal edycji terminu: status, właściciel, data, notatka + „Zakończ". */
export function EditDeadlineModal({ deadline, onClose, canManage }: EditDeadlineModalProps) {
  const toast = useToast()
  const lawyers = useLawyers(Boolean(deadline) && canManage)
  const update = useUpdateDeadline()
  const complete = useCompleteDeadline()

  const [status, setStatus] = useState<DeadlineStatus>('pending')
  const [ownerId, setOwnerId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!deadline) return
    setStatus(deadline.status)
    setOwnerId(deadline.owner_id ? String(deadline.owner_id) : '')
    setDueDate(deadline.due_date)
    setNote(deadline.note ?? '')
    setError(null)
  }, [deadline])

  if (!deadline) return null

  const save = async () => {
    setError(null)
    try {
      await update.mutateAsync({
        id: deadline.id,
        status,
        owner_id: ownerId ? Number(ownerId) : null,
        due_date: dueDate,
        note: note.trim() ? note.trim() : null,
      })
      toast.success('Termin zapisany')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się zapisać terminu.')
    }
  }

  const finish = async () => {
    setError(null)
    try {
      await complete.mutateAsync(deadline.id)
      toast.success('Termin zakończony')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.userMessage : 'Nie udało się zakończyć terminu.')
    }
  }

  const busy = update.isPending || complete.isPending

  return (
    <Modal
      open
      onClose={onClose}
      title={deadline.title}
      subtitle={[
        deadline.case ? `${deadline.case.signature} · ${deadline.case.client_name}` : 'brak przypisania',
        formatDate(deadline.due_date),
        deadline.legal_basis,
      ]
        .filter(Boolean)
        .join(' · ')}
      footer={
        <>
          {canManage && deadline.status !== 'done' ? (
            <Button variant="secondary" onClick={finish} loading={complete.isPending} disabled={busy}>
              Zakończ
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            {canManage ? 'Anuluj' : 'Zamknij'}
          </Button>
          {canManage ? (
            <Button variant="primary" onClick={save} loading={update.isPending} disabled={busy}>
              Zapisz
            </Button>
          ) : null}
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Status"
          value={status}
          disabled={!canManage}
          onChange={(event) => setStatus(event.target.value as DeadlineStatus)}
          options={STATUS_OPTIONS}
        />
        <Select
          label="Właściciel"
          value={ownerId}
          disabled={!canManage}
          onChange={(event) => setOwnerId(event.target.value)}
          options={[
            { value: '', label: 'nieprzypisany' },
            ...(lawyers.data ?? []).map((lawyer) => ({
              value: String(lawyer.id),
              label: lawyer.full_name,
            })),
          ]}
        />
        <Input
          label="Data terminu"
          type="date"
          value={dueDate}
          disabled={!canManage}
          onChange={(event) => setDueDate(event.target.value)}
        />
        <div className="col-span-2">
          <Textarea
            label="Notatka"
            rows={3}
            value={note}
            disabled={!canManage}
            placeholder="Ustalenia, zapas bezpieczeństwa, kto przygotowuje pismo…"
            onChange={(event) => setNote(event.target.value)}
          />
        </div>
      </div>
      {error ? <p className="mt-3 text-micro text-danger">{error}</p> : null}
    </Modal>
  )
}
